import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { cloudApi, type GcpSetupInput, deploymentApi, projectCloudApi, stackApi, type GcpPricingDatabase, type GcpSize, type StackTier } from "@/lib/api/cloud";

/** `all` prefixes every cloud query (status and per-project clouds), so one invalidation refreshes them all. */
export const cloudKeys = { all: ["cloud"] as const, status: ["cloud", "status"] as const };

/** The organization's cloud connections (default first) — projects deploy to the default unless they pick one. `watching` checks every few seconds. */
export function useCloudStatus(watching = false) {
  return useQuery({
    queryKey: cloudKeys.status,
    queryFn: async () => (await cloudApi.status()).data,
    refetchInterval: watching ? 5000 : false,
  });
}

/** Live Google Cloud prices (24 h server cache) for a stack in `region`. An error means the catalog is unavailable — show that, not a guess. */
export function useGcpPricing(input: { region: string; database: GcpPricingDatabase; size: GcpSize }, enabled = true) {
  return useQuery({
    queryKey: [...cloudKeys.all, "pricing", "gcp", input.region, input.database, input.size] as const,
    queryFn: async () => (await cloudApi.gcpPricing(input)).data,
    enabled: enabled && input.region.length > 0,
    staleTime: 60 * 60 * 1000,
    retry: false,
  });
}

/** Whether the signed-in user is an org admin, so the UI can hide change controls. The server enforces it either way. */
export function useIsOrgAdmin(): boolean {
  const { data } = useCloudStatus();
  return data?.isOrgAdmin ?? false;
}

/** Start one-click Google Cloud: resolves to Google's sign-in link. */
export function useGcpAuthorize() {
  return useMutation({
    mutationFn: async (input: { projectId?: string; region?: string } = {}) => (await cloudApi.gcpAuthorize(input)).data.url,
  });
}

/** Trade Google's one-time code for the sealed sign-in the wizard keeps. */
export function useGcpStart() {
  return useMutation({
    mutationFn: async (input: { code: string; state: string }) => (await cloudApi.gcpStart(input)).data,
  });
}

/** Projects the signed-in Google account can see (wizard step 3). */
export function useGcpProjects(session: string | null) {
  return useQuery({
    queryKey: [...cloudKeys.all, "gcp", "projects", session] as const,
    queryFn: async () => (await cloudApi.gcpProjects({ session: session ?? "" })).data,
    enabled: !!session,
    retry: false,
  });
}

/** Billing accounts the signed-in account can see, and whether `projectId` already has billing on. */
export function useGcpBilling(session: string | null, projectId?: string) {
  return useQuery({
    queryKey: [...cloudKeys.all, "gcp", "billing", session, projectId ?? null] as const,
    queryFn: async () => (await cloudApi.gcpBilling({ session: session ?? "", ...(projectId ? { projectId } : {}) })).data,
    enabled: !!session,
    retry: false,
  });
}

/** Which setup roles the Google account holds on `projectId` (or in general), with a request to send an owner if not. */
export function useGcpPermissions(session: string | null, projectId?: string) {
  return useQuery({
    queryKey: [...cloudKeys.all, "gcp", "permissions", session, projectId ?? null] as const,
    queryFn: async () => (await cloudApi.gcpPermissions({ session: session ?? "", ...(projectId ? { projectId } : {}) })).data,
    enabled: !!session,
    retry: false,
  });
}

/** Start the background setup with the choices made after signing in. */
export function useStartGcpSetup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: GcpSetupInput) => (await cloudApi.gcpSetupStart(input)).data,
    onSuccess: (job) => queryClient.setQueryData([...cloudKeys.all, "gcp", "job"], job),
  });
}

/** The setup job's progress; checks every 2 seconds while it runs, so a reload picks it back up. */
export function useGcpSetupJob(enabled = true) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: [...cloudKeys.all, "gcp", "job"] as const,
    queryFn: async () => {
      const job = (await cloudApi.gcpSetupJob()).data;
      if (job?.status === "succeeded") void queryClient.invalidateQueries({ queryKey: cloudKeys.status });
      return job;
    },
    enabled,
    refetchInterval: (query) => (query.state.data?.status === "running" ? 2000 : false),
  });
}

/** The "Connect AWS" or "Deploy to Azure" link. */
export function useCloudLink() {
  return useMutation({
    mutationFn: async (input: { provider: "aws"; region: string } | { provider: "azure" }) =>
      (input.provider === "aws" ? await cloudApi.awsLink(input.region) : await cloudApi.azureLink()).data.url,
  });
}

export function useConnectGcp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { projectId: string; projectNumber: string; region: string }) => cloudApi.connectGcp(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: cloudKeys.status }),
  });
}

/** Rename a connection; refreshes the list and any project that shows its name. */
export function useRenameCloud() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; name: string }) => (await cloudApi.rename(input.id, input.name)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: cloudKeys.all }),
  });
}

/** Make a connection the organization's default; projects that follow the default move with it. */
export function useSetDefaultCloud() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await cloudApi.setDefault(id)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: cloudKeys.all }),
  });
}

/** Test an Azure connection: token exchange, variable sync and spend read, each reported separately. */
export function useCheckAzure() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await cloudApi.azureCheck(id)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: cloudKeys.all }),
  });
}

/** Set or clear (null) an Azure connection's monthly spend cap. Admins only. */
export function useSetCloudBudget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; budgetUsd: number | null }) => (await cloudApi.setBudget(input.id, input.budgetUsd)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: cloudKeys.all }),
  });
}

/** Remove a connection; projects that used it fall back to the organization's default. */
export function useRemoveCloud() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => cloudApi.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: cloudKeys.all }),
  });
}

export const stackKeys = { project: (teamId: string) => ["stack", teamId] as const };

/** A project's stack (dev, or dev + stage + prod) and what it still needs. */
/** Checks every 10 seconds while `watching` (e.g. a teardown is running). */
export function useProjectStack(teamId: string, watching = false) {
  return useQuery({
    queryKey: stackKeys.project(teamId),
    queryFn: async () => (await stackApi.get(teamId)).data,
    refetchInterval: watching ? 10_000 : false,
  });
}

export function useSetProjectStack(teamId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tier: StackTier) => stackApi.set(teamId, tier),
    onSuccess: (res) => {
      queryClient.setQueryData(stackKeys.project(teamId), res.data);
    },
  });
}

export const deploymentKeys = { project: (teamId: string) => ["deployments", teamId] as const };

/** Starts tearing down stage or prod. The stack and its deploys refresh so the blocked downgrade can clear. */
export function useStackTeardown(teamId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ environment, confirm }: { environment: string; confirm: string }) =>
      stackApi.teardown(teamId, environment, confirm),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: stackKeys.project(teamId) }),
        queryClient.invalidateQueries({ queryKey: deploymentKeys.project(teamId) }),
      ]),
  });
}

/** Each environment's latest deploy; checks every few seconds while one is running. */
export function useProjectDeployments(teamId: string, enabled: boolean, watching: boolean) {
  return useQuery({
    queryKey: deploymentKeys.project(teamId),
    queryFn: async () => (await deploymentApi.list(teamId)).data,
    enabled,
    refetchInterval: (query) =>
      watching || (query.state.data ?? []).some((d) => d.state === "queued" || d.state === "in_progress") ? 5000 : false,
  });
}

export function useStartDeployment(teamId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (environment: string) => deploymentApi.start(teamId, environment),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: deploymentKeys.project(teamId) }),
  });
}

/** A project's cloud lives under `cloudKeys.all`, so changes to the organization's clouds refresh it too. */
export const projectCloudKeys = { project: (teamId: string) => [...cloudKeys.all, "project", teamId] as const };

/** The cloud this project deploys to: its own choice, or the organization's default. */
export function useProjectCloud(teamId: string) {
  return useQuery({
    queryKey: projectCloudKeys.project(teamId),
    queryFn: async () => (await projectCloudApi.get(teamId)).data,
    enabled: !!teamId,
  });
}

/** Choose one of the organization's clouds for this project, or null for its default. */
export function useSetProjectCloud(teamId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (cloudConnectionId: string | null) => (await projectCloudApi.set(teamId, cloudConnectionId)).data,
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: projectCloudKeys.project(teamId) }),
        queryClient.invalidateQueries({ queryKey: stackKeys.project(teamId) }),
        queryClient.invalidateQueries({ queryKey: deploymentKeys.project(teamId) }),
      ]),
  });
}
